import { acessoUtils } from './util/acesso.utils';
import IndexedDataBaseTable from './model/idb-tabela.js'
export class IndexedDataBase {
	#database; //Instancia global do DB Aberto, é fechada após um timeout desde a última conexão;
	#db_name; //Nome do banco da instancia;
	#db_version; //Versão do banco da instancia;
	#tabelas; //Array de ObjectStores (Tabelas) do banco;
	#crm; //CRM do cliente utilizado na instância;
	#db_name_crm; //Gerado automaticamente, uma concatenação do nome do banco de dados com o crm da base;
	#closeTimeoutTimer = 15000; //Tempo que leva para o banco fechar após a última conexão;
	#closeTimeout; //Instancia do timer para que o banco feche;

	constructor(DB_NAME, DB_VERSION, TABELAS) {
		this.#crm = JSON.stringify(acessoUtils.adquirirCRMDoUsuarioLogado());
		this.#db_name = DB_NAME;
		this.#db_name_crm = `${this.#db_name}-${this.#crm}`;
		this.#db_version = DB_VERSION;
		this.#tabelas = TABELAS;

		this.#iniciarTabelas().then(async () => {
			const init = await this.init();
		});
	}

	/**
	 * Namespare para função de inicialização do banco,
	 * por padrão não faz nada.
	 */
	async init() {
		// Implements init();
	}

	/**
	 * Namespace para função de update do banco,
	 * por padrão apenas cria os stores;
	 * @param {IDBOpenDBRequest} db - Conexão aberta com o banco a ser atualizado;
	 */
	update(db) {
		// namespace para implementação de metodos para o update de versão dos idbs.
		// é executado sempre que o getDB identifica a necessidade de update,de forma sincrona.
		// Implementação padrão apenas atualiza os object stores da tabela.
		this.createUpgradeObjectStore(db);
	}

	/**
	 * Abre e retorna o indexedDB da instancia/crm;
	 * @returns {IDBRequest<IDBDatabase>} Retorna banco de dados aberto;
	 */
	async getDB() {
		return new Promise((resolve, reject) => {
			if(!this.#isConfiguracaoValida) {
				return reject("IDB: Configurações invalidas");
			}

			if(this.#database) { return resolve(this.#database); }
			
			let request = window.indexedDB.open(this.#db_name_crm, this.#db_version);

			request.onerror = e => {
				console.warn('Erro ao iniciar IDB:', this.#db_name_crm, e);
				this.closeDB();
				reject('Error');
			};
	
			request.onsuccess = e => {
				this.#database = e.target.result;
				this.closeDB();
				resolve(this.#database);
			};
			
			request.onupgradeneeded = e => {
				let db = e.target.result;
				const update = this.update(db);
			};
		});
	}

	/**
	 * Verifica se a instancia do DB aberta existe, se existir, 
	 * reseta/cria um contador para o fechamento da conexão.
	 * 
	 * Evita que um fechamento manual do DB seja obrigatório,
	 * sem causar a necessidade de abrir e fechar a conexão varias vezes.
	 */
	closeDB() {
		if(!!this.#database) {
			clearTimeout(this.#closeTimeout);
			this.#closeTimeout = setTimeout(() => {
				this.#database.close();
				this.#database = null;
			},this.#closeTimeoutTimer)
		}
	}

	/**
	 * Ao receber uma instancia aberta do IndexedDB aguardando atualização, 
	 * cria os ObjectStores de cada tabela;
	 * @param {IDBOpenDBRequest} db - Instancia do IndexedDB a ser atualizada
	 */
	createUpgradeObjectStore(db) {
		this.#tabelas.forEach(tabela => {
			db.createObjectStore(tabela.name, tabela.options);
		})
	}
	
	/**
	 * Remove Objeto do ObjectStore (Tabela);
	 * @param {String} tabela - Nome do ObjectStore (Tabela) de onde o objeto
	 * deve ser removido;
	 * @param {Object} objeto - Objeto a ser removido
	 * @returns {Object | String} Retorna objeto que foi removido ou uma string
	 * com uma mensagem de erro
	 */
	async delete(tabela, objeto) {
		let db = await this.getDB();
		return new Promise((resolve, reject) => {
			if(!!!db || !!!tabela || !!!objeto) {
				return reject('IDB: Falta informações para operação de Delete');
			}
			let transacao = db.transaction([tabela],'readwrite');
			transacao.oncomplete = () => {
				resolve(objeto);
			};

			let store = transacao.objectStore(tabela);
			store.delete(objeto);
		});	
	}

	/**
	 * Limpa todos os dados do ObjectStore (Tabela);
	 * @param {String} tabela - Nome do ObjectStore (Tabela) que deve ser limpo; 
	 * @returns {Boolean | String} Retorna true se for bem sucedido, ou
	 * uma string com uma mensagem de erro;
	 */
	async clear(tabela) {
		let db = await this.getDB();
		return new Promise((resolve, reject) => {
			if(!!!db || !!!tabela) {
				return reject('IDB: Falta informações para operação de Clear');
			}
			let transacao = db.transaction([tabela],'readwrite');
			transacao.oncomplete = () => {
				resolve(true);
			};

			let store = transacao.objectStore(tabela);
			store.clear();
		});	
	}

	/**
	 * Adquire um, ou vários, dado(s) do ObjectStore (Tabela);
	 * @param {String} tabela - Nome do ObjectStore (Tabela);
	 * @param {String | Number | Undefined} chave - Key/ID do
	 * objeto no ObjectStore (Tabela);
	 * @returns {Object | Array<Object> | String} Se uma chave
	 * for informada, retorna o Objeto referente aquela chave;
	 * Se nada for informado, retornna uma array com todos os
	 * objetos do Store;
	 * Se houver um erro, retorna uma String com a mensagem de
	 * erro;
	 */
	async get(tabela, chave) {
		let db = await this.getDB();
		return new Promise((resolve, reject) => {
			if(!!!db || !!!tabela) {
				return reject('IDB: Falta informações para operação de Get');
			}

			let transacao = db.transaction([tabela],'readonly');
			let store = transacao.objectStore(tabela);

			if(!!chave) {
				let item = store.get(chave);
				transacao.oncomplete = () => {
					resolve(item);
				};
				item.onsuccess = e => {
					item = item.result;
				};
			} else {
				let dados = [];
				transacao.oncomplete = () => {
					resolve(dados);
				};
				store.openCursor().onsuccess = e => {
					let cursor = e.target.result;
					if (cursor) {
						dados.push(cursor.value)
						cursor.continue();
					}
				};
			}
		});
	}

	/**
	 * Salva um objeto no ObjectStore (Tabela)
	 * @param {String} tabela - Nome ddo ObjectStore (Tabela);
	 * @param {Object} objeto - Objeto a ser salvo;
	 * @returns {Object | String} Retorna o Objeto salvo se for
	 * concluído com sucesso, ou uma String com a mensagem de erro
	 * em caso de erro;
	 */
	async save(tabela, objeto) {
		let db = await this.getDB();
		return new Promise((resolve, reject) => {
			if(!!!db || !!!tabela || !!!objeto) {
				return reject('IDB: Falta informações para operação de Get');
			}

			let transacao = db.transaction([tabela],'readwrite');
			transacao.oncomplete = () => {
				resolve(objeto);
			};
			let store = transacao.objectStore(tabela);
			store.put(objeto);
		});
	}

	/**
	 * Gera uma data equivalente ao dia de hoje e as adições 
	 * informadas nos paramêtros;
	 * @param {Number} plusDay - Dias a serem adicionados
	 * após a data de "hoje";
	 * @param {Number} plusMonth - Meses a serem adicionados
	 * após a data de "hoje"; 
	 * @param {Number} plusYear - Anos a serem adicionados
	 * após a data de "hoje"; 
	 * @returns {Date} Retorna a Data com as adições informadas; 
	 */
	generateExpirationDate(plusDay = 0, plusMonth = 0, plusYear = 0) {
		const today = new Date();
		const day = today.getDate() + plusDay;
		const month = today.getMonth() + plusMonth;
		const year = today.getFullYear() + plusYear;
		const expirationDate = new Date(year, month, day);
		return expirationDate;
	}

	/**
	 * Verifica se o nome do IndexedDB da instância é
	 * válido;
	 * @returns {Boolean} 
	 */
	#isDBNAMEValido() {
		const isValido = !!this.#db_name;
		return isValido;
	}

	/**
	 * Verifica se a versão do IndexedDB da instância é
	 * válida;
	 * @returns {Boolean} 
	 */
	#isDBVERSIONValido() {
		const isValido = !!this.#db_version && parseInt(this.#db_version) > 0;
		return isValido;
	}

	/**
	 * Verifica se o ObjectStores (Tabelas) do IndexedDB da instância é/são
	 * válido(s);
	 * @returns {Boolean} 
	 */
	#isDBTABELAValido() {
		const isValido = !!this.#tabelas && Array.isArray(this.#tabelas) && this.#tabelas.length > 0;
		return isValido;
	}

	/**
	 * Verifica se a configuração da instância é válida
	 * @returns {Boolean} 
	 */
	#isConfiguracaoValida() {
		const isValido = this.#isDBNAMEValido && this.#isDBVERSIONValido && this.#isDBTABELAValido;
		return isValido;
	}

	/**
	 * Caso as ObjectStore(Tabelas) informadas sejam apenas Strings,
	 * gera um objeto valido com as configurações padrão do projeto.
	 * @returns {Array<IndexedDataBaseTable>} Retorna um Array com todas
	 * as tabelas em formato válido; 
	 */
	async #iniciarTabelas() {
		if(!this.#isDBTABELAValido) return null;
		const tabelasConvertidas = [];
		this.#tabelas.forEach(tabela => {
			if(typeof tabela === 'object' && tabela !== null) {
				tabelasConvertidas.push(tabela)
			} else {
				tabelasConvertidas.push(new IndexedDataBaseTable(tabela))
			}
		})
		this.#tabelas = tabelasConvertidas;
		return this.#tabelas;
	}
}